// The ways a document can be signed here, and with which signing tool
// (backend sharing-runbook §7.2, share-prompt tasks 11-12). Drawn from what
// signature-gateway answers GET /api/remotesign/signer?docId= (SG6): through
// a share exactly what the sharer granted, for the caller's own document
// their own signing tools in its workspace plus the app-based kinds - only
// what the document's rights allow. Several face-verified tools may be
// listed (a cloud signature and a signature protected by a cloud seal, or
// several of one kind): each is a choice of its own, never "the first one".
//
// A kind is the means a signature is made with (billing-runbook §3): GEO_EID
// is the Georgian eID application (Unitool); a face-verified tool signs on
// the gateway after the signatory's face verification.
// `rights` only says whether the caller may sign now.
import { translateService } from "../../services/imports";

export const EID_KIND = "GEO_EID";

// the workspace's Georgian eID signer, as the gateway lists it once it exists
const isEidSigner = (s) => s.kind === EID_KIND || (s.signatoryAuthTypes || []).includes("EID");

// How a way to sign reads, in the Sign menu and on the signing page: its
// title (the signing tool's name; for eID the card), the kind beside a tool's
// name, and what it means. m: a choice, or a signer as signerMethod gives it.
export const methodTitle = (m) => (m.eid ? translateService.t("sign.eid") : m.name);
export const methodKind = (m) => (m.eid ? null : translateService.t(`signature.type_${m.type}`) || null);
export const methodHint = (m) =>
  m.eid ? translateService.t("sign.hint.eid") : translateService.t(`sign.hint.${m.kind}`) || translateService.t("sign.hint.face");

// a signing process's signer (RemoteSigner) as a way to sign
export const signerMethod = (s) => ({ eid: isEidSigner(s), name: s.name, type: s.type, kind: s.kind });

// The choices, eID first, then the face-verified tools in the gateway's
// order: { key, eid, target, authType, name, type, kind, disabled, reason }.
// A target is what POST /api/remotesign/process takes: { signerId: <UUID> }
// or { signerKind }. A tool that cannot verify a required signatory is a
// disabled choice with its reason, never hidden.
// options: signing-options; docSigners: the gateway's { signers, kinds }.
export function signingChoices(options, docSigners, faceEnabled = !!globalConfig.face_sign_enabled) {
  if (!options || !docSigners) return [];
  const rights = options.rights || {};
  // a required signatory signs only with a method that verifies the person
  const limited = !!options.requiredSignatory;
  const usable = (entry) => !limited || entry.supportsLimits === true;
  const kinds = docSigners.kinds || [];
  const signers = (docSigners.signers || []).filter((s) => s.uuid);
  const choices = [];

  // eID, one choice: the GEO_EID kind whenever listed (starting by it makes
  // the workspace's eID signer if there is none yet); else the eID signer
  // itself - a share may grant eID only by that signer's uuid
  if (rights.geoIdCard) {
    const kind = kinds.find((k) => k.kind === EID_KIND && usable(k));
    const signer = signers.find((s) => isEidSigner(s) && usable(s));
    const eid = { key: "eid", eid: true, authType: "EID", name: null, type: "APP", kind: EID_KIND };
    if (kind) choices.push({ ...eid, target: { signerKind: EID_KIND }, disabled: false });
    else if (signer) choices.push({ ...eid, target: { signerId: signer.uuid }, disabled: false });
    else if (kinds.some((k) => k.kind === EID_KIND) || signers.some(isEidSigner))
      choices.push({ ...eid, target: null, disabled: true, reason: "cannotVerify" });
  }

  if (rights.faceBiometrics && faceEnabled)
    signers
      .filter((s) => !isEidSigner(s) && (s.signatoryAuthTypes || []).includes("FACE"))
      .forEach((s) =>
        choices.push({
          key: s.uuid,
          eid: false,
          target: { signerId: s.uuid },
          authType: "FACE",
          name: s.name,
          type: s.type,
          kind: s.kind,
          disabled: !usable(s),
          reason: usable(s) ? null : "cannotVerify",
        })
      );
  return choices;
}

// a required signatory as { country, personalNumber }, or null when anyone
// may sign; a value masked entirely ("•••") gives personalNumber null
export function requiredSignatoryParts(requiredSignatory) {
  if (!requiredSignatory) return null;
  const number = requiredSignatory.attributes && requiredSignatory.attributes.personalNumber;
  return {
    country: requiredSignatory.country,
    personalNumber: number && number !== "•••" ? number : null,
  };
}
